import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Flame, ShieldCheck, Anchor } from "lucide-react";
import { Marquee } from "@/components/Marquee";
import { SERVICES, IMAGES, GALLERY, SITE } from "@/data/site";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden grain">
        <img
          src="/hero.jpg"
          alt="JCD Customs & Marine shop front with hot rods, Whangamata"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-24 pt-40 w-full">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="label-industrial text-ember mb-6"
            data-testid="hero-eyebrow"
          >
            {SITE.tagline} — Whangamata, Coromandel, NZ
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-display text-6xl sm:text-7xl lg:text-9xl leading-[0.9] tracking-wide text-white max-w-4xl"
            data-testid="hero-heading"
          >
            BUILT TO BE <span className="text-ember">DRIVEN.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-8 text-gray-300 text-base md:text-lg max-w-xl"
            data-testid="hero-subtext"
          >
            Hot rods, servicing, WOF, tyre alignment, light engineering and marine — the Whangamata workshop that does the lot.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-book-btn"
              className="label-industrial bg-ember text-white px-8 py-4 hover:bg-ember-hover hover:-translate-y-1 transition-[background-color,transform] duration-150"
            >
              Book a Job
            </Link>
            <Link
              to="/services"
              data-testid="hero-services-btn"
              className="label-industrial border border-ember text-ember px-8 py-4 hover:bg-ember hover:text-white hover:-translate-y-1 transition-[background-color,color,transform] duration-150"
            >
              Our Services
            </Link>
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* Services preview */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="flex items-end justify-between mb-16 flex-wrap gap-6">
          <div>
            <p className="label-industrial text-ember mb-4">What we do</p>
            <h2 className="font-display text-5xl lg:text-7xl tracking-wide text-white" data-testid="services-preview-heading">
              ONE SHOP. EVERY JOB.
            </h2>
          </div>
          <Link
            to="/services"
            data-testid="services-preview-all-link"
            className="label-industrial text-gray-300 hover:text-ember transition-colors duration-150 flex items-center gap-2"
          >
            All services <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10" data-testid="services-preview-grid">
          {SERVICES.slice(0, 6).map((s, i) => (
            <motion.div
              key={s.slug}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className="group relative bg-[#111] overflow-hidden"
              data-testid={`service-card-${s.slug}`}
            >
              <div className="h-56 overflow-hidden">
                <img
                  src={s.image}
                  alt={s.title}
                  loading="lazy"
                  className="w-full h-full object-cover grayscale-[40%] group-hover:grayscale-0 group-hover:scale-105 transition-[filter,transform] duration-300"
                />
              </div>
              <div className="p-8 border-t-2 border-ember">
                <h3 className="font-display text-3xl tracking-wide text-white mb-3">{s.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{s.blurb}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Why us strip */}
      <section className="border-y border-white/10 bg-[#111] relative grain">
        <div className="max-w-7xl mx-auto px-6 py-24 grid grid-cols-1 md:grid-cols-3 gap-16">
          {[
            { icon: Flame, title: "HOT ROD HERITAGE", text: "Custom builds are in our blood. From chassis to paint, we build machines that turn heads at the beach." },
            { icon: ShieldCheck, title: "WOF & SAFETY FIRST", text: "Certified inspections and honest repairs. If it doesn't need fixing, we won't bill you for it." },
            { icon: Anchor, title: "MARINE READY", text: "Boats, trailers and outboards serviced so your summer on the Coromandel never misses a tide." },
          ].map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              data-testid={`why-us-${i}`}
            >
              <f.icon className="w-8 h-8 text-ember mb-6" />
              <h3 className="font-display text-2xl tracking-wider text-white mb-3">{f.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{f.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Gallery teaser */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="flex items-end justify-between mb-16 flex-wrap gap-6">
          <div>
            <p className="label-industrial text-ember mb-4">From the floor</p>
            <h2 className="font-display text-5xl lg:text-7xl tracking-wide text-white" data-testid="gallery-teaser-heading">
              RECENT WORK
            </h2>
          </div>
          <Link
            to="/gallery"
            data-testid="gallery-teaser-link"
            className="label-industrial text-gray-300 hover:text-ember transition-colors duration-150 flex items-center gap-2"
          >
            Full gallery <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="gallery-teaser-grid">
          {GALLERY.slice(0, 4).map((g, i) => (
            <Link key={i} to="/gallery" className="group relative aspect-square overflow-hidden border border-white/10" data-testid={`gallery-teaser-${i}`}>
              <img
                src={g.src}
                alt={g.caption}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300" />
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden border-t border-white/10">
        <img src={IMAGES.engineering} alt="Welding sparks" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/80" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32">
          <h2 className="font-display text-5xl lg:text-8xl tracking-wide text-white max-w-3xl" data-testid="cta-heading">
            GOT A JOB FOR US? <span className="text-ember">BRING IT IN.</span>
          </h2>
          <p className="mt-6 text-gray-300 max-w-lg">
            Flick us a message or give the shop a call — we'll get you booked in and sorted.
          </p>
          <Link
            to="/contact"
            data-testid="cta-book-btn"
            className="inline-block mt-10 label-industrial bg-ember text-white px-10 py-5 hover:bg-ember-hover hover:-translate-y-1 transition-[background-color,transform] duration-150"
          >
            Make an Enquiry
          </Link>
        </div>
      </section>
    </div>
  );
}
